import React, { useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Stack,
    Typography,
} from "@mui/material";

const BatchDownloadPdfsModal = ({
                                    open,
                                    onClose,
                                    patientIds = [],
                                    facilityName = "",
                                    token = "",
                                }) => {
    const [modalPatientIds, setModalPatientIds] = useState([]);
    const [downloadArchivedPdfs, setDownloadArchivedPdfs] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [downloadError, setDownloadError] = useState("");
    const [downloadSuccess, setDownloadSuccess] = useState("");

    useEffect(() => {
        if (!open) return;

        setModalPatientIds(
            Array.isArray(patientIds)
                ? patientIds.map((id) => String(id).trim()).filter(Boolean)
                : []
        );

        setDownloadArchivedPdfs(false);
        setDownloadLoading(false);
        setDownloadError("");
        setDownloadSuccess("");
    }, [open, patientIds]);

    const safeFileNamePart = (value) => {
        const cleaned = String(value || "facility")
            .trim()
            .replace(/[^a-z0-9-_]+/gi, "_")
            .replace(/_+/g, "_")
            .replace(/^_|_$/g, "");

        return cleaned || "facility";
    };

    const getFileNameFromHeaders = (resp) => {
        const disposition = resp.headers.get("content-disposition") || "";
        const match = disposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i);

        if (match?.[1]) return decodeURIComponent(match[1]);
        if (match?.[2]) return match[2];

        return `consent-pdfs-${safeFileNamePart(facilityName)}.zip`;
    };

    const handleDownloadZip = async () => {
        setDownloadError("");
        setDownloadSuccess("");

        if (!token) {
            setDownloadError("Missing token. Cannot download PDFs.");
            return;
        }

        if (modalPatientIds.length === 0) {
            setDownloadError("No patient IDs were found for this download.");
            return;
        }

        setDownloadLoading(true);

        try {
            const payload = {
                facility_name: String(facilityName || "").trim(),
                download_all_pdfs: downloadArchivedPdfs === true,
                patient_ids: modalPatientIds,
            };

            const resp = await fetch(`${process.env.REACT_APP_API_URL}/consent/get_consent_pdfs`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });

            if (!resp.ok) {
                let errorText = "";

                try {
                    const data = await resp.json();
                    errorText = data?.message || data?.error || "";
                } catch {
                    errorText = await resp.text().catch(() => "");
                }

                setDownloadError(
                    errorText || `Download failed: ${resp.status} ${resp.statusText}`
                );
                return;
            }

            const blob = await resp.blob();

            if (!blob || blob.size === 0) {
                setDownloadError("The server returned an empty ZIP file.");
                return;
            }

            const fileName = getFileNameFromHeaders(resp);
            const blobUrl = URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();

            setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);

            setDownloadSuccess("ZIP file download started.");
        } catch (e) {
            setDownloadError(e?.message || "Unable to download consent PDFs.");
        } finally {
            setDownloadLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={downloadLoading ? undefined : onClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>
                Batch Download Consent PDFs
            </DialogTitle>

            <DialogContent dividers>
                <Stack spacing={2}>
                    {downloadError && (
                        <Alert severity="error">
                            {downloadError}
                        </Alert>
                    )}

                    {downloadSuccess && (
                        <Alert severity="success">
                            {downloadSuccess}
                        </Alert>
                    )}

                    <Box>
                        <Typography sx={{ fontWeight: 700 }}>
                            Facility
                        </Typography>

                        <Typography color="text.secondary">
                            {facilityName || "—"}
                        </Typography>
                    </Box>

                    <Box>
                        <Typography sx={{ fontWeight: 700 }}>
                            Patients selected
                        </Typography>

                        <Typography color="text.secondary">
                            {modalPatientIds.length} patient IDs will be included.
                        </Typography>
                    </Box>

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={downloadArchivedPdfs}
                                onChange={(e) => setDownloadArchivedPdfs(e.target.checked)}
                                disabled={downloadLoading}
                            />
                        }
                        label="Download archived PDFs"
                    />

                    <Typography variant="body2" color="text.secondary">
                        Leaving this unchecked will download only the current consent PDFs.
                    </Typography>
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    disabled={downloadLoading}
                >
                    Close
                </Button>

                <Button
                    onClick={handleDownloadZip}
                    variant="contained"
                    disabled={downloadLoading || modalPatientIds.length === 0}
                    startIcon={downloadLoading ? <CircularProgress size={16} /> : null}
                >
                    {downloadLoading ? "Creating ZIP..." : "Download ZIP"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BatchDownloadPdfsModal;